import { useAuth } from '@/contexts/AuthContext';
import {
    clampOnboardingCurrentStep,
    createInitialOnboardingData
} from '@/components/Onboarding/onboardingFlow';
import { buildOnboardingRoutePreservingInvite } from '@/components/Onboarding/inviteRedirect';
import { getShopifyOnboardingResumeWorkspaceId } from '@/contexts/AuthContext/shopifyOnboardingResume';
import { VerifyAuthResponse } from '@common/api/authService';
import { useUserStorage } from '@common/hooks/useUserStorage';
import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shouldDelayOnboardingRedirect } from './onboardingRedirectGate';
import {
    hasPendingPasswordSetup,
    isAuthPasswordSetupRoute,
} from '@/utils/pendingPasswordSetup';
import { readPaymentReturnWorkspaceId } from '@/utils/paymentReturnWorkspace';
import {
    getPendingWorkspaceReturnId,
    isOnboardingSuccessfulWorkspaceReturn,
} from './AuthContext/workspaceReturnUtils';

interface OnboardingData {
  workspaceId?: string;
  currentStep?: number;
  pendingConnectionRequest?: {
    platformId: string;
    provider: string;
  } | null;
  storeConnectionPlatform?: string | null;
  storeConnectionStoreUrl: string;
  storeConnectionStoreHash: string;
  storeConnectionAccountUuid: string;
  industry: string;
  otherIndustry: string;
  businessName: string;
  logo: File | string | null;
  logoUrl?: string | null;
  workspaceSettings?: Record<string, any> | null;
  connectedStoreName: string;
  connectedStoreLogoUrl?: string | null;
  connectedIntegrations: string[];
  connectedIntegrationTrialRequirements?: Record<string, 'checkout_required'>;
  trialEligibilityState?: string | null;
  intent?: string;
  otherIntent?: string;
  plan: string;
  billingCycle: string;
  isTrialStarted: boolean;
}

interface OnboardingContextType {
  startOnboarding: () => void;
  stopOnboarding: () => void;
  nextStep: () => void;
  prevStep: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
  isAcceptingInvite: boolean;
  setIsAcceptingInvite: (isAccepting: boolean) => void;
  // 新增：自动跳转逻辑的控制
  checkOnboardingStatus: (profile: VerifyAuthResponse | null, isLoadingProfile: boolean) => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};

const ONBOARDING_STORAGE_KEY = 'onboarding_state';

const normalizeOnboardingData = (data?: OnboardingData | null): OnboardingData => {
    const nextData = {
        ...createInitialOnboardingData(),
        ...(data || {}),
    };

    return {
        ...nextData,
        currentStep: clampOnboardingCurrentStep(nextData.currentStep),
    };
};

const isOnboardingPath = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    return window.location.pathname.endsWith('/onboarding');
};

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const {
        get, set, remove
    } = useUserStorage();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        profile,
        isLoadingProfile,
        currentWorkspace,
        isLoadingWorkspaces,
    } = useAuth();

    const [onboardingData, setOnboardingData] = useState<OnboardingData>(() => {
        const savedState = get<OnboardingData>(ONBOARDING_STORAGE_KEY);
        return normalizeOnboardingData(savedState);
    });
    const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);

    const updateOnboardingData = useCallback((data: Partial<OnboardingData>) => {
        setOnboardingData(prev => {
            const newData = normalizeOnboardingData({ ...prev, ...data });
            set(ONBOARDING_STORAGE_KEY, newData);
            return newData;
        });
    }, [set]);

    const startOnboarding = () => {
        const initialData = normalizeOnboardingData(createInitialOnboardingData());
        setIsAcceptingInvite(false);
        setOnboardingData(initialData);
        set(ONBOARDING_STORAGE_KEY, initialData);

        // 跳转到 onboarding 页面
        if (!isOnboardingPath()) {
            navigate(buildOnboardingRoutePreservingInvite(window.location.search));
        }
    };

    useEffect(() => {
        if (!isOnboardingPath()) {
            setIsAcceptingInvite(false);
            return;
        }

        const searchParams = new URLSearchParams(location.search);
        const resumeWorkspaceId = getShopifyOnboardingResumeWorkspaceId(location.search);
        const resumeStep = searchParams.get('resume_step')?.trim();
        if (!resumeWorkspaceId || (resumeStep && resumeStep !== 'connect')) {
            return;
        }

        setOnboardingData(prev => {
            if (
                prev.workspaceId === resumeWorkspaceId
                && prev.currentStep === 1
            ) {
                return prev;
            }

            const nextData = normalizeOnboardingData({
                ...createInitialOnboardingData(),
                workspaceId: resumeWorkspaceId,
                currentStep: 1,
            });
            set(ONBOARDING_STORAGE_KEY, nextData);
            return nextData;
        });
    }, [location.search, set]);

    const stopOnboarding = () => {
        remove(ONBOARDING_STORAGE_KEY);
        setIsAcceptingInvite(false);
        setOnboardingData(createInitialOnboardingData());
    };

    const nextStep = useCallback(() => {
        setOnboardingData(prev => {
            const newData = normalizeOnboardingData({
                ...prev,
                currentStep: (prev.currentStep ?? 0) + 1,
            });
            set(ONBOARDING_STORAGE_KEY, newData);
            return newData;
        });
    }, [set]);

    const prevStep = useCallback(() => {
        setOnboardingData(prev => {
            const newData = normalizeOnboardingData({
                ...prev,
                currentStep: (prev.currentStep ?? 0) - 1,
            });
            set(ONBOARDING_STORAGE_KEY, newData);
            return newData;
        });
    }, [set]);

    // 新增：检查用户是否需要 onboarding
    const checkOnboardingStatus = useCallback((profile: VerifyAuthResponse | null, isLoadingProfile: boolean) => {
        // 未登录，不处理跳转，由 AuthContext 或路由守卫处理
        if(!isLoadingProfile && !profile){
            return;
        }

        if(!isLoadingProfile && profile) {
            if (!profile.workspace) {
                const searchParams = new URLSearchParams(window.location.search);
                const pendingOnboardingWorkspaceReturn = isOnboardingSuccessfulWorkspaceReturn(searchParams)
                    && Boolean(getPendingWorkspaceReturnId(searchParams, readPaymentReturnWorkspaceId()));
                if (shouldDelayOnboardingRedirect({
                    profileHasWorkspace: false,
                    currentWorkspaceId: currentWorkspace?.id,
                    isLoadingWorkspaces,
                    pendingPasswordSetup: isAuthPasswordSetupRoute(window.location.pathname) && hasPendingPasswordSetup(),
                    pendingWorkspaceReturn: pendingOnboardingWorkspaceReturn,
                })) {
                    return;
                }

                // 无 workspace，需要 onboarding
                if (!isOnboardingPath()) {
                    navigate(buildOnboardingRoutePreservingInvite(window.location.search));
                }
            }
        }
    }, [currentWorkspace?.id, isLoadingWorkspaces, navigate]);
    
    // Check onboarding status when profile changes
    useEffect(() => {
        checkOnboardingStatus(profile, isLoadingProfile);
    }, [profile, isLoadingProfile, checkOnboardingStatus]);

    const value = {
        startOnboarding,
        stopOnboarding,
        nextStep,
        prevStep,
        onboardingData,
        updateOnboardingData,
        isAcceptingInvite,
        setIsAcceptingInvite,
        checkOnboardingStatus
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};
